import React, { Suspense, lazy, useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'

/**
 * Loaded on demand, for two reasons.
 *
 * It pulls in @mui/x-date-pickers, which is heavy and which nobody needs until
 * they choose "Jump to a specific date". And it is a `.js` file containing JSX:
 * CRA's babel handles that, but Vitest's esbuild does not, so a static import
 * put it in the module graph of every test that touches the importing barrel
 * and broke three unrelated suites at collection time.
 */
const DatePicker = lazy(() => import('components/controls/DatePicker'))

export interface JumpToDateDialogProps {
	open: boolean
	onClose: () => void
	/** Called with the chosen day. */
	onPick: (date: Date) => void
	/** Oldest and newest days that actually hold content, for the hint line. */
	earliest?: string
	latest?: string
	/** What the range describes, e.g. "conversation" or "library". */
	noun?: string
	/** Explains where the jump lands when the chosen day itself is empty. */
	emptyDayHint?: string
}

/**
 * "Jump to a specific date", opened from a date pill's menu.
 *
 * Wraps the shared MUI-based DatePicker rather than introducing a second date
 * control, so the calendar matches every other date field in the app.
 */
const JumpToDateDialog = ({
	open,
	onClose,
	onPick,
	earliest,
	latest,
	noun = 'conversation',
	emptyDayHint = 'If nothing was added that day, it opens at the next day that has something.',
}: JumpToDateDialogProps) => {
	const [date, setDate] = useState<Date | null>(null)

	const confirm = () => {
		if (!date) return
		onPick(date)
		onClose()
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>Jump to a specific date</DialogTitle>
					<DialogDescription>
						{earliest && latest
							? `This ${noun} runs from ${earliest} to ${latest}.`
							: 'Pick a day to jump to.'}
					</DialogDescription>
				</DialogHeader>

				<div className="py-2">
					<Suspense fallback={<div className="h-10 animate-pulse rounded-md bg-gray-100" />}>
						<DatePicker
							name="jumpToDate"
							label="Date"
							size="small"
							error=""
							value={date}
							onChange={(e: any) => setDate(e.target.value)}
						/>
					</Suspense>
					<p className="mt-2 text-xs text-gray-400">{emptyDayHint}</p>
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose}>
						Cancel
					</Button>
					<Button variant="primary" disabled={!date} onClick={confirm}>
						Jump
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default JumpToDateDialog
